import * as React from 'react'
import axios from 'axios'
import { useLocation } from 'react-router-dom'

import { useBootstrapStore } from '../../stores/useBootstrapStore'
import {
    useOnboardingStore,
    selectGuideWaitsForPersona,
} from '../../stores/useOnboardingStore'
import { usePaywallStore, selectIsFreeTier } from '../Paywall/usePaywallStore'
import { useSnackbar } from '../Snackbar'
import { sendToAnalytics, setAnalyticsPeopleProperty } from '../../Analytics'
import ToastCard from '../OnboardingGuide/ToastCard'
import {
    ACTION_BUTTON,
    BODY_TEXT,
    SECONDARY_TEXT,
    TITLE_TEXT,
    TOAST_FOOTER,
    choiceChipClass,
} from '../OnboardingGuide/toastStyles'
import {
    ANSWERED_VALUE,
    PERSONA_OPTIONS,
    answeredKeyFor,
    suggestPersona,
} from './personas'

// One question, once, on /apps: what the user came for. For a first-session
// user it is the first thing the guide asks — the guide's own first toast
// waits for the answer and then follows the persona's flow. Everyone else
// (guide done, dismissed or never eligible) gets it on their next /apps visit.
// The only way past it is a pick, but it stays a toast: the page around it
// keeps working. The answer goes to the profile (PATCH /me/profile), to the
// guide (useOnboardingStore.setPersona) and to analytics as a person
// property. A free account is then reminded once to upgrade (UpgradeReminder).

function readAnswered(username) {
    try {
        return (
            window.localStorage.getItem(answeredKeyFor(username)) ===
            ANSWERED_VALUE
        )
    } catch (error) {
        return false
    }
}

function writeAnswered(username) {
    try {
        window.localStorage.setItem(answeredKeyFor(username), ANSWERED_VALUE)
    } catch (error) {
        // Private mode: the profile holds the answer, so one profile read
        // keeps the question away.
    }
}

function track(action, data) {
    try {
        sendToAnalytics({
            userId: getUsername(),
            event: 'persona_toast',
            data: { action, ...data },
        })
        window.posthog?.capture?.('persona_toast', { action, ...data })
    } catch (ignored) {}
}

function getUsername() {
    return useBootstrapStore.getState().user?.username
}

export default function PersonaToast({ prefill }) {
    const location = useLocation()
    const bootState = useBootstrapStore(state => state.bootState)
    const user = useBootstrapStore(state => state.user)
    const guideStatus = useOnboardingStore(state => state.status)
    const guideWaits = useOnboardingStore(selectGuideWaitsForPersona)
    const showSnackbar = useSnackbar(state => state.showSnackbar)

    const [profile, setProfile] = React.useState(undefined) // undefined: not read yet
    const [persona, setPersona] = React.useState(null)
    const [state, setState] = React.useState('idle') // idle | saving | closed
    const [prefilled, setPrefilled] = React.useState(false)

    const onApps = /^\/apps\/?$/.test(location.pathname)
    const eligibleUser = Boolean(
        bootState === 'ready' && user?.isAuthenticated && !user?.isPublic
    )
    const username = user?.username

    // Either the guide is waiting on the answer, or it is out of the way.
    // 'unknown' means the store has not resolved yet; a guide mid-loop keeps
    // the question for later.
    const guideOutOfTheWay =
        guideStatus === 'done' ||
        guideStatus === 'dismissed' ||
        guideStatus === 'ineligible'
    const mayAsk = guideWaits || guideOutOfTheWay

    React.useEffect(() => {
        if (!onApps || !eligibleUser || !mayAsk) return
        if (profile !== undefined) return
        // Answered on an earlier visit, and no guide to feed.
        if (readAnswered(username) && !guideWaits) {
            setProfile(null)
            return
        }
        let cancelled = false
        const feedGuide = (value, options) => {
            if (guideWaits) {
                useOnboardingStore.getState().setPersona(value, options)
            }
        }
        axios
            .get('/me/profile')
            .then(response => {
                if (cancelled) return
                const loaded = (response.data && response.data.profile) || {}
                if (loaded.persona) {
                    // Chosen before, on another device or in another browser.
                    feedGuide(loaded.persona, { source: 'profile' })
                    setProfile(loaded)
                    return
                }
                setProfile(loaded)
                const suggestion = suggestPersona({
                    landingPath: prefill && prefill.landingPath,
                    acquisitionChannel: loaded.acquisitionChannel,
                })
                setPersona(suggestion)
                setPrefilled(Boolean(suggestion))
                track('shown', { prefilled: suggestion })
                // Read now, so the pick never waits on it.
                usePaywallStore.getState().loadUsage()
            })
            .catch(() => {
                if (cancelled) return
                // The guide must not wait on a profile that won't answer:
                // this session runs the default flow, the question stays
                // open for the next visit.
                feedGuide(null, { source: 'unavailable', persist: false })
                setProfile(null)
            })
        return () => {
            cancelled = true
        }
    }, [onApps, eligibleUser, mayAsk, guideWaits, profile, username, prefill])

    if (!onApps || !eligibleUser || state === 'closed') return null
    if (!profile || profile.persona) return null

    async function save() {
        if (!persona) return
        setState('saving')
        try {
            await axios.patch('/me/profile', { persona })
        } catch (error) {
            setState('idle')
            showSnackbar('error', 'We could not save that. Please try again.')
            return
        }
        writeAnswered(username)
        setAnalyticsPeopleProperty({
            persona,
            persona_source: 'self_reported',
        })
        const suggestion = suggestPersona({
            landingPath: prefill && prefill.landingPath,
            acquisitionChannel: profile.acquisitionChannel,
        })
        const wasPrefilled = prefilled && persona === suggestion
        track('picked', { persona, prefilled: wasPrefilled })

        await usePaywallStore.getState().loadUsage()
        const onboardingStore = useOnboardingStore.getState()
        if (selectIsFreeTier(usePaywallStore.getState())) {
            onboardingStore.openUpgradeReminder()
        }
        onboardingStore.setPersona(persona, {
            source: 'picked',
            prefilled: wasPrefilled,
        })
        setState('closed')
    }

    return (
        <ToastCard
            eyebrow={guideWaits ? 'First-Time User Guide' : 'Quick question'}
            role="dialog"
            modal
            labelledBy="persona-toast-title"
            testId="persona-toast"
        >
            {/* A <p>, not a heading: preflight is off, so a bare <h2> inherits the legacy heading rules. */}
            <p
                id="persona-toast-title"
                className={`tw-m-0 tw-mb-2 ${TITLE_TEXT}`}
            >
                What brings you here?
            </p>
            <p className={`tw-m-0 ${BODY_TEXT} ${SECONDARY_TEXT}`}>
                Please select your use case so we can customize your
                InfraNodus experience.
            </p>
            <div
                role="radiogroup"
                aria-label="What brings you here"
                className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2"
            >
                {PERSONA_OPTIONS.map(option => {
                    const selected = persona === option.code
                    return (
                        <button
                            key={option.code}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            title={option.hint || undefined}
                            onClick={() => setPersona(option.code)}
                            data-testid={`persona-option-${option.code}`}
                            className={choiceChipClass(selected)}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
            <div className={`${TOAST_FOOTER} tw-justify-end`}>
                <button
                    type="button"
                    onClick={save}
                    disabled={!persona || state === 'saving'}
                    data-testid="persona-toast-save"
                    className={ACTION_BUTTON}
                >
                    {state === 'saving' ? 'Saving…' : 'Continue'}
                </button>
            </div>
        </ToastCard>
    )
}
